// OrgChipIcon.tsx
import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';

type Org = {
  name?: string;
  icon_url?: string | null; // URL to access the organization icon
};

type Props = {
  organization?: Org | null;
  /** Matches Chip sizes: 'small' => 18px, 'medium' => 20px */
  size?: 'small' | 'medium';
  /** Additional sx if you want to tweak */
  sx?: SxProps<Theme>;
  /** MUI Chip will inject this; we must forward it */
  className?: string;
};

/** Utility: get initials from a name */
function getInitials(name?: string) {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]?.toUpperCase() ?? '').join('');
}

const OrgChipIcon = React.forwardRef<HTMLDivElement, Props>(
  ({ organization, size = 'medium', sx, className }, ref) => {
    const px = size === 'small' ? 18 : 20;

    const src = organization?.icon_url || undefined;

    return (
      <Box
        ref={ref}
        className={className} // important: lets Chip style this as an icon
        sx={{
          // Chip expects the icon to be square and inline
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: px,
          height: px,
          // prevent shrink weirdness in tight layouts
          flexShrink: 0,
          // allow consumer overrides
          ...((sx as object) || {}),
        }}
      >
        <Avatar
          // className='avatar'
          src={src}
          alt={organization?.name || 'Organization'}
          sx={theme => ({
            width: px,
            height: px,
            fontSize: px * 0.55,
            bgcolor:
              src === undefined
                ? theme.palette.primary.light
                : theme.palette.common.white,
            '& .MuiAvatar-img': {
              height: 'auto',
              width: '100%',
              objectFit: 'cover', 
            },
          })}
        >
          {getInitials(organization?.name)}
        </Avatar>
      </Box>
    );
  }
);

OrgChipIcon.displayName = 'OrgChipIcon';
export default OrgChipIcon;
